import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';

interface UseCase {
  id: string;
  title: string;
  description: string;
  icon: string;
  tags: string[];
  benefits: string[];
  industryIds: string[];
}

@Component({
  selector: 'app-use-cases',
  templateUrl: './use-cases.component.html',
  styleUrls: ['./use-cases.component.scss'],
  standalone: false
})
export class UseCasesComponent implements OnInit, AfterViewInit {
  @ViewChild('useCasesCarousel') carousel!: ElementRef<HTMLDivElement>;
  
  useCases: UseCase[] = [
    {
      id: 'customer-support',
      title: 'Atención al cliente 24/7',
      description: 'Responde consultas frecuentes, resuelve dudas y proporciona soporte constante sin intervención humana.',
      icon: 'bi bi-headset',
      tags: ['Soporte', 'Atención al cliente'],
      benefits: [
        'Reduce el tiempo de respuesta a segundos',
        'Libera al equipo de atención de consultas repetitivas',
        'Escalado automático a agentes humanos cuando es necesario'
      ],
      industryIds: ['ecommerce', 'finance', 'healthcare', 'hospitality']
    },
    {
      id: 'appointment-booking',
      title: 'Gestión de reservas y citas',
      description: 'Automatiza el proceso de reservas, reduce errores y ofrece confirmaciones instantáneas a tus clientes.',
      icon: 'bi bi-calendar-check',
      tags: ['Reservas', 'Citas', 'Automatización'],
      benefits: [
        'Integración con cualquier calendario',
        'Recordatorios automáticos de citas',
        'Reducción de no-shows en un 35%'
      ],
      industryIds: ['healthcare', 'hospitality', 'realestate']
    },
    {
      id: 'training-assistant',
      title: 'Asistente de capacitación',
      description: 'Complementa tus programas de formación con un asistente virtual que resuelve dudas y refuerza conocimientos.',
      icon: 'bi bi-mortarboard',
      tags: ['Educación', 'Formación', 'E-learning'],
      benefits: [
        'Respuestas instantáneas a preguntas frecuentes',
        'Personalización del aprendizaje según el estudiante',
        'Disponibilidad 24/7 para consultas'
      ],
      industryIds: ['education', 'finance', 'healthcare']
    },
    {
      id: 'lead-qualification',
      title: 'Calificación de leads',
      description: 'Identifica y califica leads automáticamente mediante conversaciones naturales que recopilan información clave.',
      icon: 'bi bi-funnel',
      tags: ['Ventas', 'Marketing', 'Leads'],
      benefits: [
        'Calificación de leads 24/7 sin intervención humana',
        'Integración con CRM para seguimiento inmediato',
        'Aumento promedio del 28% en conversión de leads'
      ],
      industryIds: ['ecommerce', 'finance', 'realestate']
    },
    {
      id: 'product-recommendations',
      title: 'Recomendaciones personalizadas',
      description: 'Sugiere productos o servicios relevantes a través de conversaciones contextuales basadas en las necesidades del cliente.',
      icon: 'bi bi-gift',
      tags: ['E-commerce', 'Ventas', 'Personalización'],
      benefits: [
        'Aumento del valor medio del carrito en un 15-25%',
        'Experiencia de compra personalizada y conversacional',
        'Recomendaciones basadas en el contexto de la conversación'
      ],
      industryIds: ['ecommerce', 'hospitality', 'finance']
    },
    {
      id: 'faq-automation',
      title: 'Automatización de FAQ',
      description: 'Responde automáticamente a preguntas frecuentes sobre productos, servicios, políticas o procedimientos.',
      icon: 'bi bi-question-circle',
      tags: ['Soporte', 'Automatización', 'FAQ'],
      benefits: [
        'Reducción del 70% en consultas básicas al equipo de soporte',
        'Base de conocimiento centralizada y siempre actualizada',
        'Experiencia de usuario consistente'
      ],
      industryIds: ['ecommerce', 'finance', 'healthcare', 'education', 'hospitality', 'realestate']
    },
    {
      id: 'employee-onboarding',
      title: 'Onboarding de empleados',
      description: 'Facilita la incorporación de nuevos empleados respondiendo preguntas sobre políticas, procedimientos y recursos de la empresa.',
      icon: 'bi bi-person-plus',
      tags: ['RRHH', 'Onboarding', 'Capacitación'],
      benefits: [
        'Reducción del 40% en consultas a RRHH durante el proceso de incorporación',
        'Experiencia consistente para todos los nuevos empleados',
        'Acceso inmediato a información relevante las 24 horas'
      ],
      industryIds: ['finance', 'healthcare', 'education', 'technology', 'hospitality']
    },
    {
      id: 'marketing-campaigns',
      title: 'Campañas de marketing conversacional',
      description: 'Interactúa con usuarios durante campañas promocionales, ofreciendo información personalizada y facilitando la conversión.',
      icon: 'bi bi-megaphone',
      tags: ['Marketing', 'Conversión', 'Campañas'],
      benefits: [
        'Aumento del 32% en tasa de engagement con clientes potenciales',
        'Recopilación cualificada de datos de contacto',
        'Seguimiento de campaña personalizado y automático'
      ],
      industryIds: ['ecommerce', 'hospitality', 'realestate', 'finance']
    },
    {
      id: 'multilingual-support',
      title: 'Soporte multilingüe',
      description: 'Atiende a clientes en múltiples idiomas sin necesidad de personal especializado, facilitando la expansión a mercados internacionales.',
      icon: 'bi bi-translate',
      tags: ['Internacional', 'Idiomas', 'Globalización'],
      benefits: [
        'Soporte en más de 20 idiomas sin costos adicionales',
        'Detección automática del idioma del usuario',
        'Experiencia consistente independientemente del idioma'
      ],
      industryIds: ['ecommerce', 'hospitality', 'finance', 'technology']
    },
    {
      id: 'order-tracking',
      title: 'Seguimiento de pedidos',
      description: 'Permite a los clientes consultar y recibir actualizaciones sobre el estado de sus pedidos a través de una interfaz conversacional.',
      icon: 'bi bi-box-seam',
      tags: ['Logística', 'E-commerce', 'Atención al cliente'],
      benefits: [
        'Reducción del 65% en consultas sobre estado de pedidos',
        'Notificaciones proactivas sobre cambios de estado',
        'Integración con sistemas de gestión de inventario y logística'
      ],
      industryIds: ['ecommerce', 'hospitality']
    },
    {
      id: 'payment-assistance',
      title: 'Asistencia en pagos',
      description: 'Guía a los usuarios en el proceso de pago, resolviendo dudas y facilitando la finalización de transacciones.',
      icon: 'bi bi-credit-card',
      tags: ['Finanzas', 'Pagos', 'Transacciones'],
      benefits: [
        'Reducción del 25% en abandonos durante el proceso de pago',
        'Respuestas inmediatas a preguntas sobre métodos de pago y seguridad',
        'Asistencia en tiempo real para problemas de transacción'
      ],
      industryIds: ['ecommerce', 'finance', 'hospitality', 'healthcare']
    },
    {
      id: 'event-registration',
      title: 'Registro y gestión de eventos',
      description: 'Automatiza la inscripción a eventos, proporciona información y gestiona cambios o cancelaciones a través de conversaciones naturales.',
      icon: 'bi bi-calendar-event',
      tags: ['Eventos', 'Registro', 'Automatización'],
      benefits: [
        'Proceso de registro simplificado accesible 24/7',
        'Envío automático de recordatorios e información previa al evento',
        'Gestión eficiente de cambios y cancelaciones'
      ],
      industryIds: ['education', 'hospitality', 'technology']
    },
    {
      id: 'feedback-collection',
      title: 'Recopilación de feedback',
      description: 'Obtén retroalimentación de clientes a través de conversaciones naturales que incrementan la tasa de respuesta y la calidad de los comentarios.',
      icon: 'bi bi-chat-square-text',
      tags: ['Feedback', 'Mejora continua', 'Experiencia de cliente'],
      benefits: [
        'Aumento del 40% en tasas de respuesta comparado con formularios tradicionales',
        'Análisis de sentimiento y detección de patrones automática',
        'Seguimiento personalizado de problemas identificados'
      ],
      industryIds: ['ecommerce', 'hospitality', 'healthcare', 'finance', 'education']
    },
    {
      id: 'technical-support',
      title: 'Soporte técnico inicial',
      description: 'Proporciona un primer nivel de asistencia técnica, solucionando problemas comunes y escalando casos complejos a especialistas.',
      icon: 'bi bi-tools',
      tags: ['Soporte técnico', 'Resolución de problemas', 'IT'],
      benefits: [
        'Resolución de hasta un 60% de problemas técnicos sin intervención humana',
        'Recopilación estructurada de información para diagnóstico avanzado',
        'Disponibilidad inmediata las 24 horas, reduciendo tiempos de espera'
      ],
      industryIds: ['technology', 'ecommerce', 'finance']
    },
    {
      id: 'insurance-claims',
      title: 'Asistente de reclamaciones',
      description: 'Guía a los clientes en el proceso de reclamaciones de seguros, recopilando información necesaria y proporcionando actualizaciones de estado.',
      icon: 'bi bi-shield-check',
      tags: ['Seguros', 'Reclamaciones', 'Finanzas'],
      benefits: [
        'Reducción del 30% en tiempo de procesamiento de reclamaciones',
        'Mejora en la precisión de la información recopilada',
        'Actualizaciones proactivas sobre el estado de las reclamaciones'
      ],
      industryIds: ['finance', 'healthcare', 'realestate']
    },
    {
      id: 'content-recommendation',
      title: 'Recomendación de contenido',
      description: 'Sugiere contenido relevante (artículos, videos, recursos) basado en las necesidades e intereses expresados por el usuario durante la conversación.',
      icon: 'bi bi-collection-play',
      tags: ['Contenido', 'Personalización', 'Engagement'],
      benefits: [
        'Aumento del 45% en el tiempo de permanencia en el sitio',
        'Mayor engagement con contenido educativo y de marketing',
        'Personalización basada en interacciones previas y preferencias'
      ],
      industryIds: ['education', 'finance', 'healthcare', 'technology']
    }
  ];

  
  filteredUseCases: UseCase[] = [];
  allTags: string[] = [];
  currentFilter: string = 'all';
  activeTagFilters: string[] = [];
  selectedUseCaseId: string = '';
  
  // Variables para el carrusel
  carouselPosition: number = 0;
  maxCarouselScroll: number = 0;
  cardWidth: number = 0;
  visibleCards: number = 1;
  currentPageIndex: number = 0;
  
  constructor() { }
  
  ngOnInit(): void {
    this.extractAllTags();
    this.filterByTag('all');
  }
  
  ngAfterViewInit(): void {
    this.initCarousel();
    // Establecer el primer caso de uso como seleccionado por defecto
    if (this.filteredUseCases.length > 0) {
      this.selectedUseCaseId = this.filteredUseCases[0].id;
    }
  }
  
  @HostListener('window:resize')
  onResize(): void {
    this.initCarousel();
  }
  
  initCarousel(): void {
    if (this.carousel?.nativeElement) {
      const carouselEl = this.carousel.nativeElement;
      const firstCardEl = carouselEl.querySelector('.use-case');
      
      if (firstCardEl) {
        this.cardWidth = firstCardEl.clientWidth + 16; // width + margin
        
        // Calcular cuántas tarjetas son visibles a la vez
        this.visibleCards = Math.floor(carouselEl.clientWidth / this.cardWidth);
        
        // Calcular la máxima posición de scroll
        this.maxCarouselScroll = (this.filteredUseCases.length * this.cardWidth) - carouselEl.clientWidth;
        
        // Ajustar por si el resize reduce la cantidad de casos visibles
        if (this.maxCarouselScroll < 0) this.maxCarouselScroll = 0;
      }
    }
  }
  
  extractAllTags(): void {
    const tagsSet = new Set<string>();
    
    this.useCases.forEach(useCase => {
      useCase.tags.forEach(tag => tagsSet.add(tag));
    });
    
    this.allTags = Array.from(tagsSet);
  }
  
  filterByTag(tag: string): void {
    this.currentFilter = tag;
    
    if (tag === 'all') {
      this.filteredUseCases = [...this.useCases];
      this.activeTagFilters = [];
    } else {
      this.filteredUseCases = this.useCases.filter(useCase => 
        useCase.tags.includes(tag)
      );
      this.activeTagFilters = [tag];
    }
    
    // Resetear la posición del carrusel
    this.scrollToPosition(0);
    this.currentPageIndex = 0;
    
    // Recalcular los límites del carrusel
    setTimeout(() => {
      this.initCarousel();
      
      // Seleccionar el primer caso de uso de la lista filtrada
      if (this.filteredUseCases.length > 0) {
        this.selectedUseCaseId = this.filteredUseCases[0].id;
      }
    }, 100);
  }
  
  selectUseCase(useCaseId: string): void {
    this.selectedUseCaseId = useCaseId;
    
    // Actualizar los filtros activos basados en el caso de uso seleccionado
    const selectedUseCase = this.useCases.find(uc => uc.id === useCaseId);
    if (selectedUseCase) {
      this.activeTagFilters = [...selectedUseCase.tags];
    }
    
    // Hacer scroll al caso de uso seleccionado
    const index = this.filteredUseCases.findIndex(uc => uc.id === useCaseId);
    if (index !== -1) {
      this.goToPage(Math.floor(index / this.visibleCards));
    }
  }
  
  scrollCarousel(direction: 'left' | 'right'): void {
    if (!this.carousel?.nativeElement) return;
    
    const carouselEl = this.carousel.nativeElement;
    const scrollAmount = this.visibleCards * this.cardWidth;
    
    if (direction === 'left') {
      const newPosition = Math.max(0, this.carouselPosition - scrollAmount);
      this.scrollToPosition(newPosition);
    } else {
      const newPosition = Math.min(this.maxCarouselScroll, this.carouselPosition + scrollAmount);
      this.scrollToPosition(newPosition);
    }
    
    this.updatePageIndex();
  }
  
  scrollToPosition(position: number): void {
    if (!this.carousel?.nativeElement) return;
    
    const carouselEl = this.carousel.nativeElement;
    carouselEl.scrollLeft = position;
    this.carouselPosition = position;
  }
  
  updatePageIndex(): void {
    if (!this.carousel?.nativeElement) return;
    
    const totalPages = Math.ceil(this.filteredUseCases.length / this.visibleCards);
    this.currentPageIndex = Math.floor(this.carouselPosition / (this.visibleCards * this.cardWidth));
    
    // Asegurarse de que el índice esté en el rango correcto
    if (this.currentPageIndex >= totalPages) {
      this.currentPageIndex = totalPages - 1;
    }
    
    if (this.currentPageIndex < 0) {
      this.currentPageIndex = 0;
    }
  }
  
  goToPage(index: number): void {
    if (!this.carousel?.nativeElement) return;
    
    const totalPages = Math.ceil(this.filteredUseCases.length / this.visibleCards);
    
    // Verificar que el índice está en el rango correcto
    if (index < 0) index = 0;
    if (index >= totalPages) index = totalPages - 1;
    
    this.currentPageIndex = index;
    const position = index * this.visibleCards * this.cardWidth;
    this.scrollToPosition(position);
  }
  
  requestDemo(useCaseId: string, event?: Event): void {
    // Detener la propagación para evitar la selección del caso de uso
    if (event) {
      event.stopPropagation();
    }
    
    // En una implementación real, esta función abriría un modal o navegaría a una página de demostración
    console.log(`Demo solicitada para caso de uso: ${useCaseId}`);
    // Ejemplo: this.router.navigate(['/demo'], { queryParams: { useCaseId } });
  }
  
  contactSales(): void {
    // En una implementación real, esta función abriría un formulario de contacto o navegaría a una página de contacto
    console.log('Contactar con ventas');
    // Ejemplo: this.router.navigate(['/contacto']);
  }
}